// src/lib/apiClient.ts

import { supabase } from '@/integrations/supabase/client';
import { classifyError, getUserMessage } from '@/lib/errors';
import type { ApiError } from '@/lib/errors';
import { API_BASE_URL } from '@/lib/apiBaseUrl';

interface ApiOptions extends RequestInit {
  requiresAuth?: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  details?: string;
}

function isApiEnvelope<T>(value: unknown): value is ApiResponse<T> {
  return typeof value === 'object' && value !== null && 'success' in value;
}

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async getAuthToken(): Promise<string | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  }

  private async buildHeaders(options?: ApiOptions): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-store',
    };

    // Account-binding headers must be merged, never replace the authenticated headers.
    new Headers(options?.headers).forEach((value, key) => {
      const previous = Object.keys(headers).find(name => name.toLowerCase() === key);
      if (previous) delete (headers as Record<string, string>)[previous];
      (headers as Record<string, string>)[key] = value;
    });

    if (options?.requiresAuth !== false) {
      const token = await this.getAuthToken();
      if (token) {
        // CAST: HeadersInit doesn't support string indexing
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  async get<T>(endpoint: string, options?: ApiOptions): Promise<T> {
    const headers = await this.buildHeaders(options);
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      method: 'GET',
      cache: 'no-store',
      headers,
    });

    if (!response.ok) {
      throw await this.buildApiError(response, endpoint);
    }

    return await this.parseJsonResponse<T>(response, endpoint);
  }

  /** Authenticated binary reads use the same Hector session boundary as JSON reads. */
  async getBlob(endpoint: string, options?: ApiOptions): Promise<Blob> {
    const headers = await this.buildHeaders(options);
    const response = await fetch(`${this.baseUrl}${endpoint}`, { ...options, method: 'GET', cache: 'no-store', headers });
    if (!response.ok) throw await this.buildApiError(response, endpoint);
    return response.blob();
  }

  async post<T>(endpoint: string, body?: unknown, options?: ApiOptions): Promise<T> {
    const headers = await this.buildHeaders(options);

    const isFormData = body instanceof FormData;
    const finalHeaders = { ...headers };
    if (isFormData) {
      delete (finalHeaders as Record<string, string>)['Content-Type'];
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      method: 'POST',
      cache: 'no-store',
      headers: finalHeaders,
      body: isFormData ? body : JSON.stringify(body),
    });

    if (!response.ok) {
      throw await this.buildApiError(response, endpoint);
    }

    return await this.parseJsonResponse<T>(response, endpoint);
  }

  async put<T>(endpoint: string, body?: unknown, options?: ApiOptions): Promise<T> {
    const headers = await this.buildHeaders(options);

    const isFormData = body instanceof FormData;
    const finalHeaders = { ...headers };
    if (isFormData) {
      delete (finalHeaders as Record<string, string>)['Content-Type'];
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      method: 'PUT',
      cache: 'no-store',
      headers: finalHeaders,
      body: isFormData ? body : JSON.stringify(body),
    });

    if (!response.ok) {
      throw await this.buildApiError(response, endpoint);
    }

    return await this.parseJsonResponse<T>(response, endpoint);
  }

  async patch<T>(endpoint: string, body?: unknown, options?: ApiOptions): Promise<T> {
    const headers = await this.buildHeaders(options);
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      method: 'PATCH',
      cache: 'no-store',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw await this.buildApiError(response, endpoint);
    }

    return await this.parseJsonResponse<T>(response, endpoint);
  }

  async delete<T = void>(endpoint: string, options?: ApiOptions): Promise<T> {
    const headers = await this.buildHeaders(options);

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      method: 'DELETE',
      cache: 'no-store',
      headers,
      body: options?.body,
    });

    if (!response.ok) {
      throw await this.buildApiError(response, endpoint);
    }

    try {
      return await this.parseJsonResponse<T>(response, endpoint, true);
    } catch (error) {
      if (error instanceof Error && 'statusCode' in error) throw error;
      return undefined as T;
    }
  }

  private async parseJsonResponse<T>(response: Response, endpoint: string, allowEmpty = false): Promise<T> {
    const bodyText = await response.text();
    if (!bodyText.trim()) {
      if (allowEmpty) return undefined as T;
      throw this.buildInvalidJsonResponseError(response, endpoint, 'empty_body');
    }

    let parsedBody: unknown;
    try {
      parsedBody = JSON.parse(bodyText);
    } catch {
      throw this.buildInvalidJsonResponseError(response, endpoint, 'invalid_json');
    }

    if (isApiEnvelope<T>(parsedBody) && 'data' in parsedBody) {
      return parsedBody.data as T;
    }

    return parsedBody as T;
  }

  private buildInvalidJsonResponseError(
    response: Response,
    endpoint: string,
    reason: 'empty_body' | 'invalid_json'
  ): ApiError {
    const requestId = response.headers.get('X-Request-Id') ?? undefined;
    const contentType = response.headers.get('Content-Type') ?? 'none';
    const apiError = new Error(
      `Réponse API JSON invalide pour ${endpoint}: ${reason}, status=${response.status}, content-type=${contentType}`
    ) as ApiError;
    apiError.statusCode = response.status;
    apiError.errorCode = 'INVALID_API_JSON_RESPONSE';
    apiError.requestId = requestId;
    apiError.details = {
      endpoint,
      reason,
      contentType,
    };
    return apiError;
  }

  /**
   * Construit un ApiError typé depuis une réponse HTTP non-2xx.
   *
   * Contrat backend : { error: "CODE_TECHNIQUE", message: "Texte lisible FR", requestId?, details? }
   * On lit `message` en priorité — c'est le texte lisible par l'utilisateur.
   * `error` est conservé comme errorCode (code stable pour PostHog/logs).
   * Le header X-Request-Id est capturé pour la corrélation support.
   *
   * Note : le 403 est retourné comme ApiError normale (classifié 'auth' par ErrorClassifier).
   * C'est l'appelant (composant ou hook) qui appelle ErrorService.handle() — pas buildApiError.
   * Cela évite le double toast.
   */
  private async buildApiError(response: Response, _endpoint: string): Promise<ApiError> {
    const requestId = response.headers.get('X-Request-Id') ?? undefined;
    const classification = classifyError(
      new Error(`HTTP ${response.status}`),
      response.status
    );
    // Le code HTTP et le code backend restent des métadonnées de diagnostic ;
    // le texte affichable est toujours fourni par la taxonomie centralisée.
    let userMessage = getUserMessage(
      classification.category,
      undefined,
      response.status,
      classification.reason
    );
    let errorCode: string | undefined;
    let details: Record<string, unknown> | undefined;

    try {
      const bodyText = await response.text();
      const body = bodyText ? JSON.parse(bodyText) as Record<string, unknown> : {};
      // Priorité : message lisible FR > message centralisé. `body.error` est
      // un code technique (ex. BAD_REQUEST), jamais un texte destiné à l'avocat.
      if (typeof body.message === 'string' && body.message.trim().length > 0) {
        userMessage = body.message;
      }
      errorCode = body.error as string | undefined;
      details = body.details as Record<string, unknown> | undefined;
    } catch {
      // Réponse sans JSON — message par défaut conservé
    }

    // 401 : déconnecter et rediriger immédiatement
    if (response.status === 401) {
      await supabase.auth.signOut();
      window.location.href = '/auth';
    }

    const apiError = new Error(userMessage) as ApiError;
    apiError.statusCode = response.status;
    apiError.errorCode = errorCode;
    apiError.requestId = requestId;
    apiError.details = details;

    return apiError;
  }
}

export const apiClient = new ApiClient(API_BASE_URL);

export async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}
